class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute("handle");
    this.swiperInitialized = false; // Prevent multiple initializations
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Prevent multiple initializations
    if (this.swiperInitialized) return;
    this.swiperInitialized = true;

    this.options = this.setOptions();
    this.instance = new Swiper('.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", () => this.init());
      window.addEventListener("shopify:section:select", () => this.init());

      // fallback initialization after delay in design mode
      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
      return;
    }

    // Start the gallery as soon as the element is in the DOM.
    // Waiting for window "load" delayed swipe until every page asset
    // (including large product images) finished downloading.
    this.scheduleInit();
  }

  scheduleInit() {
    let attempts = 0;
    const start = () => {
      if (this.swiperInitialized) return;
      if (typeof Swiper === "undefined") {
        // Swiper bundle is deferred; retry briefly if this file raced ahead
        attempts += 1;
        if (attempts < 60) requestAnimationFrame(start);
        return;
      }
      this.init();
    };

    // Custom element upgrade already runs after DOM parse for deferred scripts,
    // but keep a microtask so layout/siblings (e.g. thumbs) are ready.
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => queueMicrotask(start), {
        once: true
      });
    } else {
      queueMicrotask(start);
    }
  }

  disconnectedCallback() {
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Options before initialization
   */
  setOptions() {
    if (this.debug) console.log('setOptions');
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    // if (this.debug) console.log('getOptionsAsJsonScripts');
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};
    if (jsonScriptAutomated && jsonScriptAutomated.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptAutomated.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptAutomated?.textContent);
      }
    }
    if (jsonScriptOverwrite && jsonScriptOverwrite.textContent) {
      try {
        options = { ...options, ...JSON.parse(jsonScriptOverwrite.textContent) };
      } catch (error) {
        console.error('Error parsing JSON', error, jsonScriptOverwrite?.textContent);
      }
    }
    return options;
  }

  /** thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.debug) console.log('setInteractions');
  }
}
// customElements.define('instance-swiper', InstanceSwiper);
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;
    if (this.debug) console.log('setCenteredSlides');

    const afterInit = (swiper) => {
      if (swiper.activeIndex !== 0) swiper.slideTo(0);
      if (this.debug) console.log('afterInit');
      const swiperWrapper = swiper.wrapperEl;
      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });
      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
          if (this.debug) console.log('swiperWrapper observer removed styles!');
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
              if (this.debug) console.log('swiperWrapper observer disconnect!');
            }
          }, 500);
        }
      }

      /**
       * Quick fix for thumbnails dislocationing issue
       * when thumbnails are less than the height of the wrapper
       */
      const thumbsWrapper = this.parentElement;

      // Oblicza dokładną wysokość kolumny miniaturek na podstawie pozycji pierwszego i ostatniego slajdu
      const calculateAllThumbHeight = () => {
        const firstSlide = this.querySelector(".swiper-slide");
        const lastSlide = this.querySelector(".swiper-slide:last-child");
        if (!firstSlide || !lastSlide) return 0;

        const wrapperRect = thumbsWrapper.getBoundingClientRect();
        const lastRect = lastSlide.getBoundingClientRect();

        // Wysokość = od góry wrappera do dołu ostatniego slajdu
        return Math.max(lastRect.bottom - wrapperRect.top, 0);
      };

      // Udostępniamy metodę na instancji, żeby można ją było wywołać po przefiltrowaniu miniaturek
      this.setThumbsWrapperHeightToAuto = () => {
        const allThumbHeight = calculateAllThumbHeight();
        if (!allThumbHeight) {
          thumbsWrapper.style.removeProperty("height");
          thumbsWrapper.style.removeProperty("max-height");
          return;
        }

        // Na mobile pozwalamy wrapperowi rosnąć naturalnie – bez sztywnych wysokości
        if (window.innerWidth < 750) {
          thumbsWrapper.style.removeProperty("height");
          thumbsWrapper.style.removeProperty("max-height");
          return;
        }

        // Na desktopie przycinamy wrapper dokładnie do wysokości miniaturek
        const target = Math.max(allThumbHeight, 0);
        thumbsWrapper.style.height = `${target}px`;
        thumbsWrapper.style.maxHeight = `${target}px`;
      };

      window.addEventListener("resize", this.setThumbsWrapperHeightToAuto);
      // have to wait global.js media area height css variables to be generated
      setTimeout(() => {
        this.setThumbsWrapperHeightToAuto();
      }, 50);
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit,
      },
    };
  }


}
if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute("thumbs");
    this.isZoomActive = this.hasAttribute("zoom");
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
  }

  static get observedAttributes() {
    return ["thumbs", "zoom"];
  }

  /**
   * Thumbs must be mounted before the main gallery (Swiper thumbs option).
   * Custom elements are defined thumbs-first, but we still force-init thumbs here
   * so a timing race never leaves the gallery without swipe + thumb sync.
   */
  init() {
    if (this.isThumbsActive) {
      const thumbsEl = document.querySelector(
        `[handle="${this.getAttribute("thumbs")}"]`
      );
      if (thumbsEl && typeof thumbsEl.init === "function" && !thumbsEl.swiperInitialized) {
        thumbsEl.init();
      }
    }
    super.init();
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    if (this.debug) console.log("setThumbOptions");
    const thumbsInstance = super.getThumbsInstance();
    if (this.debug) console.log("thumbsInstance", thumbsInstance);
    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Interaction after initialization
   */
  setInteractions() {
    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();
    this.initModelViewer();

    // Add thumb click handler for model slides
    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      slide.addEventListener('click', () => this.enableSwiper());
    });

    // Force pagination update on mobile after each slide change
    this.instance.on("slideChange", swiper => {
      if (window.innerWidth < 750) {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated manually on mobile.");
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on("scroll", swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        swiper.pagination.update();
        if (this.debug) console.log("Pagination updated after scroll.");
      }, 200);
    });
  }

  initModelViewer() {
    // Find all swiper slides with model viewers
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        // Store references to associated elements
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        // Model viewer container click handler
        container.addEventListener('click', (e) => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        // Model viewer button click handler
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        // Prevent model-viewer direct interaction
        modelViewer.addEventListener('mousedown', (e) => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    // Add navigation click handlers
    if (this.instance.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;

      const attachNavHandler = (el) => {
        if (!el) return;

        // Swiper can return a single element or an array of elements
        const elements = Array.isArray(el) ? el : [el];

        elements.forEach((node) => {
          if (node && typeof node.addEventListener === 'function') {
            node.addEventListener('click', () => {
              this.resetAllModelViewers();
              this.enableSwiper(true);
            });
          }
        });
      };

      attachNavHandler(nextEl);
      attachNavHandler(prevEl);
    }
  }

  resetAllModelViewers() {
    // Find all model viewers in the current gallery instance
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;
    this.instance.allowTouchMove = true;
  }

  toggleModelViewer(btn, modelViewer) {
    const wasActive = btn.classList.contains('is-active');

    // Reset all model viewers in this gallery
    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      // Enable only this specific model viewer
      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    // Only disable touch/mouse dragging
    this.instance.allowTouchMove = false;

    // Re-enable after a short delay if no model interaction occurs
    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    // Always enable touch/mouse dragging if force is true (navigation/thumb clicks)
    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      // Reset model viewer state if forced
      if (force && this.isModelActive) {
        this.isModelActive = false;
        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }
        if (this.modelViewerBtn) {
          this.modelViewerBtn.classList.remove('is-active');
        }
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    if (this.debug) console.log("setThumbsInteraction");
    const thumbsSwiper = this.options.thumbs.swiper;

    // Enable swiper on thumb click
    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);
      if (this.modelViewerBtn) {
        this.modelViewerBtn.classList.remove('btn--active');
      }
    });

    this.instance.on("slideChangeTransitionStart", function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (this.debug) console.log("slideChangeTransitionStart", activeIndex, thumbsActiveIndex);
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  photoSwipeLightboxInit() {
    const [closeIcon, prevIcon, nextIcon] = [
      document.querySelector("[data-close-icon]"),
      document.querySelector("[data-prev-icon]"),
      document.querySelector("[data-next-icon]")
    ];

    function isPhone() {
      return window.innerWidth < 750;
    }

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `.photoswipe-wrapper`,
      children: "a.product__gallery-toggle",
      mainClass: "pswp--product-media-gallery",

      loop: false,
      showHideAnimationType: "fade",

      // PhotoSwipe treats .pswp__img as "the image" even when the user
      // clicks the letterboxed margin, and a truthy doubleTapAction delays
      // the first tap by 300ms (second tap then zooms). Force an immediate
      // close and do not offer a secondary click-zoom.
      bgClickAction: "close",
      imageClickAction: "close",
      tapAction: "close",
      doubleTapAction: false,
      clickToCloseNonZoomable: true,

      initialZoomLevel: "fit",
      secondaryZoomLevel: zoomLevelObject => zoomLevelObject.fit,
      maxZoomLevel: 4,

      paddingFn: () => {
        if (isPhone()) {
          return { top: 16, bottom: 16, left: 8, right: 8 };
        }
        return { top: 40, bottom: 40, left: 16, right: 16 };
      },

      appendToEl: document.body,
      pswpModule: PhotoSwipe
    });
    photoSwipeLightboxInstance.addFilter(
      "uiElement",
      (element, data) => {
        if (data.name === "close") {
          element.innerHTML = closeIcon.innerHTML;
        } else if (data.name === "arrowPrev") {
          element.innerHTML = prevIcon.innerHTML;
        } else if (data.name === "arrowNext") {
          element.innerHTML = nextIcon.innerHTML;
        }
        return element;
      }
    );

    // html for video
    photoSwipeLightboxInstance.addFilter(
      "itemData",
      (itemData, index) => {
        if (itemData.type === "html" && itemData.element) {
          return {
            html: itemData.element.dataset.pswpHtml
          };
        }
        return itemData;
      }
    );

    photoSwipeLightboxInstance.init();

    photoSwipeLightboxInstance.on("bindEvents", () => {
      const pswp = photoSwipeLightboxInstance.pswp;
      if (!pswp) return;

      pswp.options.bgClickAction = "close";
      pswp.options.imageClickAction = "close";
      pswp.options.tapAction = "close";
      pswp.options.doubleTapAction = false;

      const closeInsteadOfZoom = event => {
        event.preventDefault();
        pswp.close();
      };
      pswp.on("imageClickAction", closeInsteadOfZoom);
      pswp.on("bgClickAction", closeInsteadOfZoom);
      pswp.on("tapAction", closeInsteadOfZoom);
      pswp.on("doubleTapAction", closeInsteadOfZoom);

      this.bindLightboxArrowPosition(pswp);
    });

    photoSwipeLightboxInstance.on("openingAnimationEnd", () => {
      const pswp = photoSwipeLightboxInstance.pswp;
      this.bindLightboxArrowPosition(pswp);
      this.placeLightboxArrows(pswp);
    });
    photoSwipeLightboxInstance.on("change", () => {
      this.placeLightboxArrows(photoSwipeLightboxInstance.pswp);
    });

    photoSwipeLightboxInstance.on("beforeOpen", () => {
      document.body.classList.add("overflow-hidden");
      const videos = this.querySelectorAll("video");
      Array.from(videos).forEach(video => {
        video
          .play()
          .then(() => {})
          .catch(() => {
            video.pause();
          });
      });
    });

    photoSwipeLightboxInstance.on("closingAnimationStart", () => {
      document.body.classList.remove("overflow-hidden");
    });
  }

  bindLightboxArrowPosition(pswp) {
    if (!pswp || pswp.__gabiArrowsBound) return;
    pswp.__gabiArrowsBound = true;

    const syncArrows = () => this.placeLightboxArrows(pswp);
    pswp.on("openingAnimationEnd", syncArrows);
    pswp.on("change", syncArrows);
    pswp.on("resize", syncArrows);
    pswp.on("loadComplete", syncArrows);
    pswp.on("firstUpdate", syncArrows);
    pswp.on("contentActivate", syncArrows);

    let frame = 0;
    const tick = () => {
      syncArrows();
      frame += 1;
      if (frame < 40) requestAnimationFrame(tick);
    };
    tick();
  }

  getLightboxImageRect(pswp) {
    const slide = pswp && pswp.currSlide;
    if (!slide || !pswp.element) return null;

    const pswpRect = pswp.element.getBoundingClientRect();
    const candidates = [];

    if (slide.width && slide.currZoomLevel) {
      const width = slide.width * slide.currZoomLevel;
      const height = slide.height * slide.currZoomLevel;
      const panX = slide.pan && typeof slide.pan.x === "number" ? slide.pan.x : 0;
      const panY = slide.pan && typeof slide.pan.y === "number" ? slide.pan.y : 0;
      let left = panX;
      let top = panY;
      if (slide.holderElement) {
        const holder = slide.holderElement.getBoundingClientRect();
        left = holder.left + panX - pswpRect.left;
        top = holder.top + panY - pswpRect.top;
      }
      if (width > 8 && height > 8) {
        candidates.push({ left, top, width, height, right: left + width, bottom: top + height });
      }
    }

    const img =
      (slide.container && slide.container.querySelector("img.pswp__img")) ||
      (slide.holderElement && slide.holderElement.querySelector("img.pswp__img"));
    if (img && img.naturalWidth && img.naturalHeight) {
      const box = img.getBoundingClientRect();
      const scale = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
      const width = img.naturalWidth * scale;
      const height = img.naturalHeight * scale;
      const left = box.left + (box.width - width) / 2 - pswpRect.left;
      const top = box.top + (box.height - height) / 2 - pswpRect.top;
      if (width > 8 && height > 8) {
        candidates.push({ left, top, width, height, right: left + width, bottom: top + height });
      }
    }

    if (!candidates.length) return null;
    candidates.sort((a, b) => a.width - b.width);
    return candidates[0];
  }

  pinLightboxButton(el, left, top) {
    if (!el) return;
    el.style.setProperty("position", "absolute", "important");
    el.style.setProperty("inset", "auto", "important");
    el.style.setProperty("left", `${Math.round(left)}px`, "important");
    el.style.setProperty("right", "auto", "important");
    el.style.setProperty("top", `${Math.round(top)}px`, "important");
    el.style.setProperty("margin", "0", "important");
    el.style.setProperty("transform", "none", "important");
  }

  placeLightboxArrows(pswp) {
    if (!pswp || !pswp.element) return;

    const prev = pswp.element.querySelector(".pswp__button--arrow--prev");
    const next = pswp.element.querySelector(".pswp__button--arrow--next");
    const close = pswp.element.querySelector(".pswp__button--close");
    if (!prev && !next && !close) return;

    const rect = this.getLightboxImageRect(pswp);
    if (!rect) return;

    const inset = 8;

    if (prev || next) {
      const buttonWidth = (prev && prev.offsetWidth) || (next && next.offsetWidth) || 48;
      const buttonHeight = (prev && prev.offsetHeight) || (next && next.offsetHeight) || 48;
      const top = rect.top + rect.height / 2 - buttonHeight / 2;
      const prevLeft = rect.left + inset;
      const nextLeft = rect.right - buttonWidth - inset;

      pswp.element.style.setProperty("--gabi-pswp-prev-x", `${Math.round(prevLeft)}px`);
      pswp.element.style.setProperty("--gabi-pswp-next-x", `${Math.round(nextLeft)}px`);
      pswp.element.style.setProperty("--gabi-pswp-arrow-y", `${Math.round(top)}px`);
      pswp.element.classList.add("gabi-pswp-arrows-ready");

      this.pinLightboxButton(prev, prevLeft, top);
      this.pinLightboxButton(next, nextLeft, top);
    }

    if (close) {
      if (close.parentElement !== pswp.element) {
        pswp.element.appendChild(close);
      }
      const closeWidth = close.offsetWidth || 36;
      const closeTop = rect.top + inset;
      const closeLeft = rect.right - closeWidth - inset;

      pswp.element.style.setProperty("--gabi-pswp-close-x", `${Math.round(closeLeft)}px`);
      pswp.element.style.setProperty("--gabi-pswp-close-y", `${Math.round(closeTop)}px`);
      pswp.element.classList.add("gabi-pswp-close-ready");

      this.pinLightboxButton(close, closeLeft, closeTop);
    }
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(
      this.querySelectorAll("[data-media-id]")
    ).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}


class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();
    this.init();
    window.addEventListener("resize", this.init.bind(this));
    if (Shopify.designMode) {
      window.addEventListener("shopify:section:load", this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider').offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && window.innerWidth > 750) {
      containerOffsetWidth = document.querySelector('.main-product__media--grid-item').offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const mediaInfoTextWidth = this.querySelector('p:last-child').offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${mediaInfoTextWidth / maxWidthForInfo * 8}s`;
      mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}


